<template>
  <create-account-layout
    :current-step="2"
    title="Verification"
  >
    <form
      novalidate="novalidate"
      @submit.prevent="handleSubmit"
    >
      <section>
        <p class="mb-3 mt-8 text-left text-sm text-dark-gray">
          {{ subtitle }}
        </p>
        <div
          class="mb-3 flex flex-row gap-2 rounded-lg border border-pumpkin-400 bg-pumpkin-100 p-2 text-left"
        >
          <div>
            <svg
              class="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8.2699 3.82273C9.03875 2.49138 10.9612 2.49138 11.7294 3.82273L16.6254 12.3087C17.3936 13.64 16.4324 15.3042 14.8953 15.3042H5.10397C3.56693 15.3042 2.60637 13.64 3.37455 12.3087L8.26923 3.82273H8.2699ZM9.99998 7.3155C10.1324 7.3155 10.2594 7.3681 10.353 7.46173C10.4466 7.55536 10.4992 7.68234 10.4992 7.81475V10.311C10.4992 10.4434 10.4466 10.5704 10.353 10.664C10.2594 10.7577 10.1324 10.8103 9.99998 10.8103C9.86757 10.8103 9.74058 10.7577 9.64695 10.664C9.55332 10.5704 9.50072 10.4434 9.50072 10.311V7.81475C9.50072 7.68234 9.55332 7.55536 9.64695 7.46173C9.74058 7.3681 9.86757 7.3155 9.99998 7.3155ZM9.99998 12.8073C10.1324 12.8073 10.2594 12.7547 10.353 12.6611C10.4466 12.5674 10.4992 12.4404 10.4992 12.308C10.4992 12.1756 10.4466 12.0486 10.353 11.955C10.2594 11.8614 10.1324 11.8088 9.99998 11.8088C9.86757 11.8088 9.74058 11.8614 9.64695 11.955C9.55332 12.0486 9.50072 12.1756 9.50072 12.308C9.50072 12.4404 9.55332 12.5674 9.64695 12.6611C9.74058 12.7547 9.86757 12.8073 9.99998 12.8073Z"
                fill="#FF9330"
              />
            </svg>
          </div>
          <p class="text-xs font-normal text-pumpkin-900">
            Highway will never call you for your verification code. Please do
            not share with anyone that asks for your code.
          </p>
        </div>
        <form-field
          label="Verification Code"
          :value="phone_twilio_validation"
          placeholder="Enter Verification Code"
          :on-input="onInput"
          name="phone_twilio_validation"
          maxlength="6"
          :is-valid="!error"
          error-message="Incorrect verification code"
        />
        <div class="flex flex-col">
          <button-main
            class="my-4 mb-4"
            :disabled="!isValidCode"
            type="submit"
            :loading-spinner="isLoading"
            value="Continue"
          >
            Continue
          </button-main>
          <div class="mt-4 flex flex-col items-center">
            <p class="mb-2 text-1md text-body-color">Code didn't send?</p>
            <router-link
              class="mx-auto w-max cursor-pointer text-1md text-yellow-500"
              to="/sign-up/create-account"
            >
              Try again
            </router-link>
          </div>
        </div>
      </section>
    </form>
  </create-account-layout>
</template>

<script>
import { mapState } from 'vuex';
import CreateAccountLayout from '@/components/layouts/CreateAccountLayout.vue';
import FormField from '@/components/FormField.vue';
import ButtonMain from '@/components/Button.vue';
import { toastResponse } from '@/common/handleServerResponse';

export default {
  name: 'SignUpOrLoginVerification',
  components: {
    CreateAccountLayout,
    FormField,
    ButtonMain,
  },
  data() {
    return {
      isLoading: false,
      error: false,
      phone_twilio_validation: '',
    };
  },
  computed: {
    isIdentityVerified() {
      return this.$store.state.users.currentUser?.last_verified_identity_at;
    },
    shouldShowLegalAgreements() {
      return !this.$store.getters['users/hasConsentedToLegalAgreements'];
    },
    inquiries() {
      return this.$store.state.users.inquiries.length > 0;
    },
    isValidCode() {
      return (
        this.phone_twilio_validation &&
        this.phone_twilio_validation.length === 6
      );
    },
    isIDVOnboardingFlowActive() {
      const status =
        this.$store.state.users.currentUser?.identity_proofing_status;
      if (status === 'skip_identity_proofing') return false;
      if (status === 'require_identity_proofing') return true;
      const featureFlagEnabled = this.$store.getters['users/getFeatureFlag']?.(
        'idv_onboarding_flow'
      )?.enabled;
      return !!featureFlagEnabled;
    },
    isPhoneRequire() {
      return !this.isEmailRequiredForLogin && !this.userPhoneNumber;
    },
    isEmailRequire() {
      return this.isEmailRequiredForLogin && !this.userEmailAddress;
    },
    ...mapState({
      isEmailRequiredForLogin: (state) =>
        state.carriers?.isEmailRequiredForLogin || false,
      userPhoneNumber: (state) => state.signUpData?.phone_number || null,
      userEmailAddress: (state) => state.signUpData?.email_address || null,
    }),
    subtitle() {
      let source = 'mobile number';
      if (this.isEmailRequiredForLogin) source = 'email address';
      return `Enter the code we just sent to the ${source} you entered.`;
    },
  },
  created() {
    if (this.isPhoneRequire || this.isEmailRequire) {
      /*
       * Do not allow the user to enter the code without first registering
       * a cell phone or an email address.
       * Warning: should happen only when the page is refreshed
       */
      if (this.shouldShowLegalAgreements) return;
      this.$router.push({
        name: 'CreateAccountOrLogin',
      });
    }
  },
  methods: {
    getNextRoute(fallbackRoute) {
      let latestInquiryStatus = this.$store.getters['users/inquiryStatus'];
      let route = { name: fallbackRoute, query: {} };

      if (this.shouldShowLegalAgreements) {
        route.name = 'LegalAgreements';
        route.query = {
          redirectTo:
            this.isIDVOnboardingFlowActive &&
            latestInquiryStatus !== 'approved' &&
            latestInquiryStatus !== 'declined'
              ? 'IdentityVerification'
              : fallbackRoute,
        };
      } else if (
        this.isIDVOnboardingFlowActive &&
        latestInquiryStatus !== 'approved' &&
        latestInquiryStatus !== 'declined' &&
        !this.isIdentityVerified
      ) {
        route.name = 'IdentityVerification';
      }
      console.log(route, 'IDF', this.isIdentityVerified);
      return route;
    },
    onInput(e) {
      this.phone_twilio_validation = e.target.value;
      this.$store.commit('updateSignupField', {
        name: 'phone_twilio_validation',
        value: e.target.value,
      });
    },
    async smallCarrierVerification() {
      // verification code is valid
      // run check for open user invitation
      this.validation_error = false;
      const invitationResult = await this.$store.dispatch(
        'users/patchCheckOpenInvitation'
      );
      if ([200, 201].includes(invitationResult.status)) {
        // invitation exists
        // sent user to page that prompts them to accept/decline invitation
        await this.$store.dispatch('users/refreshCurrentUser');
        // console.log("next page");
        // this.$router.push({ name: "SignUpVerification" });
      } else if (invitationResult.status === 404) {
        // invitation does NOT exist
        // (double check error message here) "no active invitations for this user"
        // Skip ahead to company type
        console.log('#IDV-4');
        const router = this.getNextRoute('CompanyType');
        console.log(router);
        this.$router.push({
          name: router.name,
          params: { canMoveOn: true },
          query: { ...(router.query || {}) },
        });
        // this.$router.push({ name: "SignUpVerification" });
      }
    },
    // Handle a big carrier verification and check if the user
    // is already associated with a company. If they are, send
    // them on to the onboarding packet
    async bigCarrierVerification() {
      console.log('#IDV-5');
      const router = this.getNextRoute('GeneralInformation');
      this.$router.push({
        name: router.name,
        query: { ...(router.query || {}) },
      });
    },
    async userAlreadyAssociatedWithCompany() {
      console.log('User already associated with a company');

      const redirectAfterSignIn = JSON.parse(
        localStorage.getItem('redirect_url_after_sigin')
      );

      const companyType =
        this.$store.state.users.currentUser.company.company_type;
      const fallbackRouteName = (() => {
        switch (companyType) {
          case 'broker':
            return 'Broker Dashboard';
          case 'carrier':
            return 'CarrierWelcomePage';
          case 'dispatcher':
            return 'Dispatcher Dashboard';
          default:
            return 'Home';
        }
      })();

      // Case 1: `redirectAfterSignIn` exists and policies are verified
      if (redirectAfterSignIn && !this.shouldShowLegalAgreements) {
        console.log('Redirecting to redirectAfterSignIn, policies verified');
        localStorage.removeItem('redirect_url_after_sigin'); // Remove item after use
        return this.$router.push(redirectAfterSignIn);
      }

      // Case 2: `redirectAfterSignIn` exists but policies are NOT verified
      if (redirectAfterSignIn && this.shouldShowLegalAgreements) {
        console.log('Redirecting to LegalAgreements for verification');
        return this.$router.push({ name: 'LegalAgreements' });
      }

      // Case 3: `redirectAfterSignIn` does NOT exist and policies are NOT verified
      if (!redirectAfterSignIn && this.shouldShowLegalAgreements) {
        console.log('Redirecting to LegalAgreements with fallback query');
        return this.$router.push({
          name: 'LegalAgreements',
          query: { redirectTo: fallbackRouteName }, // Use `fallbackRoute` as `redirectTo`
        });
      }

      // Case 4: `redirectAfterSignIn` does NOT exist and policies are already verified
      console.log('Redirecting to fallback route, policies verified');
      return this.$router.push({ name: fallbackRouteName });
    },
    async handleSubmit() {
      if (this.$route.query.needsClaimCarrier) {
        await this.handleManualVerification();
      } else {
        await this.handleVerification();
      }
    },
    async handleVerificationResponse(res) {
      // Handle an existing user already associated with a company
      console.log(res.data);
      if (res.data['is_trying_to_claim_manual_carrier?']) {
        this.handleManualValidation(res);
        return;
      }
      if (res.data.company?.id != null) {
        return this.userAlreadyAssociatedWithCompany();
      }

      // Check if a large carrier and requires email for sign up
      if (this.isEmailRequiredForLogin) {
        await this.bigCarrierVerification(res);
      }

      // Must be a small carrier signing up
      return await this.smallCarrierVerification(res);
    },
    handleManualValidation(res) {
      const nameIsFilled = res?.data?.first_name?.length > 0;
      const router = this.getNextRoute(
        nameIsFilled ? 'CompanyDetails' : 'YourDetails'
      );

      console.log(nameIsFilled ? '#IDV-2' : '#IDV-1');

      this.$router.push({
        name: router.name,
        query: { ...(router.query || {}) },
      });
    },
    async handleVerification() {
      // check for manual carrier Data to make call to create_manual endpoint
      this.isLoading = true;
      let res = await this.$store.dispatch('users/postSignUpOrLoginCode', {
        code: this.$store.state.signUpData.phone_twilio_validation,
        ...(this.isEmailRequiredForLogin && {
          email_address: this.userEmailAddress,
        }),
        ...(!this.isEmailRequiredForLogin && {
          phone: this.userPhoneNumber,
        }),
      });

      if ([200, 201].includes(res.status)) {
        if (res.data?.qr_code?.length > 0 && res.data?.setup_key?.length > 0) {
          this.$store.commit('setTOTPData', {
            qrCode: res.data.qr_code,
            setupKey: res.data.setup_key,
          });
          return this.$router.push({ name: 'SecurityRegisterDevice' });
        }

        await Promise.all([
          this.$store.dispatch('users/getFeatureFlags', res.data),
          this.$store.dispatch('users/getUserInquiries', res.data.id),
        ]);

        this.handleVerificationResponse(res);
      } else if (res.status === 422) {
        this.validation_error = true;
        toastResponse(this, res, null);
      } else {
        this.validation_error = false;
        toastResponse(this, res, null);
      }
      this.isLoading = false;
    },
    handleUserAlreadyLoggedIn: function () {
      const companyType =
        this.$store.state.users.currentUser.company.company_type;
      switch (companyType) {
        case 'broker':
          return this.$router.push({ name: 'Broker Dashboard' });
        case 'carrier':
          return this.$router.push({ name: 'CarrierWelcomePage' });
        case 'dispatcher':
          return this.$router.push({ name: 'Dispatcher Dashboard' });
        default:
          return this.$router.push({ name: 'Home' });
      }
    },
    async handleManualVerification() {
      this.isLoading = true;
      let res = await this.$store.dispatch(
        'users/postSignUpOrLoginManualCode',
        {
          code: this.$store.state.signUpData.phone_twilio_validation,
        }
      );

      // Handle TOTP setup redirect for manual carrier verification
      if (res.data?.qr_code?.length > 0 && res.data?.setup_key?.length > 0) {
        this.$store.commit('setTOTPData', {
          qrCode: res.data.qr_code,
          setupKey: res.data.setup_key,
        });
        this.isLoading = false;
        return this.$router.push({ name: 'SecurityRegisterDevice' });
      }

      await Promise.all([
        this.$store.dispatch('users/getFeatureFlags', res.data),
        this.$store.dispatch('users/getUserInquiries', res.data.id),
      ]);

      this.handleVerificationResponse(res);
      this.isLoading = false;
      const router = this.getNextRoute('YourDetails');
      switch (res.status) {
        case 200:
          this.handleVerificationResponse(res);
          break;
        case 201:
          // carrier already claimed
          this.handleNewCarrierClaimed();
          break;
        case 206:
          console.log('#IDV-6');
          this.$router.push({
            name: router.name,
            query: { ...(router.query || {}) },
          });
          break;
        case 418:
          this.handleUserAlreadyLoggedIn();
          break;
        case 422:
        default:
          this.validation_error = true;
          toastResponse(this, res, null);
      }
    },
    async handleNewCarrierClaimed(carrierId) {
      this.isLoading = true;
      const claimedRes = await this.$store.dispatch(
        'users/hasCarrierBeenClaimed',
        carrierId
      );
      this.isLoading = false;
      if (
        [201, 200].includes(claimedRes.status) ||
        ([304].includes(claimedRes.status) && claimedRes.data?.length > 0)
      ) {
        this.$store.commit('carriers/setMaskedContacts', claimedRes.data);
        this.$router.push({
          name: 'RequestAccessFMSCA',
          params: { carrierHasBeenClaimed: true },
        });
      } else {
        toastResponse(this, claimedRes, null);
      }
    },
  },
};
</script>

<style scoped>
.button {
  @apply my-3 mb-4 bg-light-gray !important;
}
</style>
